import { useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { Link } from "@tanstack/react-router";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { LuCirclePlus, LuPencil, LuTrash2 } from "react-icons/lu";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Field,
  Heading,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  Portal,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { CardGridSkeleton } from "../components/LoadingSkeletons";
import { TableSkeleton } from "../components/TableSkeleton";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { TextLink } from "../components/TextLink";
import { readableError } from "@/lib/errors";
import {
  participationRoleLabel,
  participationStateColor,
  participationStateLabel,
  todayInVienna,
} from "./eventsShared";

type Record_ = NonNullable<ReturnType<typeof useQuery<typeof api.participations.listForPerson>>>[number];

/**
 * The other half of a person's page: what they took part in.
 *
 * Equal billing with their activities, and not a section bolted underneath
 * them, because for most of the institute this is the ONLY half with anything
 * in it -- no activities at all, and a record of workshops, conferences and
 * seminars (model §17).
 *
 * A conference is recorded here rather than inside whatever activity asked for
 * one. The record outlives the requirement: it belongs to the person's career
 * and later carries the research-output link, whether or not anything ever
 * required it.
 */
export function MyRecord({ effectivePersonId }: { effectivePersonId: Id<"persons"> | null }) {
  const rows = useQuery(api.participations.listForPerson, { personId: null, effectivePersonId });
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState<Record_ | null>(null);

  const { upcoming, past, credits } = useMemo(() => {
    const today = todayInVienna();
    const live = (rows ?? []).filter((row) => row.state !== "cancelled");
    return {
      upcoming: live.filter((row) => row.startDay >= today).sort((a, b) => a.startDay.localeCompare(b.startDay)),
      past: live.filter((row) => row.startDay < today),
      // Self-entered, and unverified by design (principle 6). Shown as the
      // person's own running total rather than as anything the institute
      // asserts.
      credits: live.reduce((sum, row) => sum + (row.credits ?? 0), 0),
    };
  }, [rows]);

  if (rows === undefined) {
    return (
      <Stack gap={8}>
        <CardGridSkeleton label="Loading your record" cards={3} columns={{ base: 1, md: 3 }} />
        <TableSkeleton label="Loading your record" rows={5} columns={4} />
      </Stack>
    );
  }

  return (
    <Stack gap={8}>
      <SimpleGrid gap={4} columns={{ base: 1, md: 3 }}>
        <Tile label="Taken part in" value={String(past.length)} detail="Seminars, workshops, conferences and courses." />
        <Tile label="Coming up" value={String(upcoming.length)} detail="What you are down for." />
        <Tile
          label="ECTS"
          value={credits === 0 ? "—" : String(credits)}
          detail="From the courses you recorded."
        />
      </SimpleGrid>

      <Stack gap={4}>
        <HStack justify="space-between" align="end" wrap="wrap" gap={3}>
          <Box>
            <Heading size="md">Conferences and courses</Heading>
            <Text mt={1} fontSize="sm" color="fg.muted">
              Conferences and courses outside CeMM. Add them yourself.
            </Text>
          </Box>
          <Button size="sm" colorPalette="blue" onClick={() => setRecording(true)}>
            <LuCirclePlus />Record one
          </Button>
        </HStack>

        {rows.filter((row) => row.external !== null).length === 0 ? (
          <Empty>Nothing recorded yet.</Empty>
        ) : (
          <Stack gap={3}>
            {rows
              .filter((row) => row.external !== null)
              .map((row) => (
                <ExternalRow key={row._id} row={row} onEdit={() => setEditing(row)} effectivePersonId={effectivePersonId} />
              ))}
          </Stack>
        )}
      </Stack>

      <Stack gap={4}>
        <Box>
          <Heading size="md">At CeMM</Heading>
          <Text mt={1} fontSize="sm" color="fg.muted">
            Seminars, workshops and institute days you signed up for.
          </Text>
        </Box>

        {rows.filter((row) => row.eventId !== null).length === 0 ? (
          <Empty>
            Nothing yet. The <TextLink><Link to="/events">calendar</Link></TextLink>{" "}
            shows what is coming up.
          </Empty>
        ) : (
          <Stack gap={3}>
            {rows
              .filter((row) => row.eventId !== null)
              .map((row) => (
                <Card.Root key={row._id} borderWidth="1px" borderColor="border">
                  <Card.Body p={4}>
                    <HStack justify="space-between" align="start" gap={4} wrap="wrap">
                      <Stack gap={1} minW="14rem" flex="1">
                        <HStack gap={2} wrap="wrap">
                          {row.event === null ? (
                            <Text fontWeight="semibold">{"An event that has been removed"}</Text>
                          ) : (
                            <Link to="/events/$eventId" params={{ eventId: row.event._id }}>
                              <Text fontWeight="semibold">{row.event.title}</Text>
                            </Link>
                          )}
                          <Badge variant="subtle">{participationRoleLabel(row.role)}</Badge>
                        </HStack>
                        <Text fontSize="sm" color="fg.muted">{row.startDay}</Text>
                      </Stack>
                      <Badge colorPalette={participationStateColor(row.state)}>
                        {participationStateLabel(row.state)}
                      </Badge>
                    </HStack>
                  </Card.Body>
                </Card.Root>
              ))}
          </Stack>
        )}
      </Stack>

      <ExternalDialog
        open={recording || editing !== null}
        existing={editing}
        effectivePersonId={effectivePersonId}
        onClose={() => { setRecording(false); setEditing(null); }}
      />
    </Stack>
  );
}

function Tile({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={2} p={6}>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.2em" color="fg.muted">
          {label}
        </Text>
        <Text fontSize="3xl">{value}</Text>
        <Text fontSize="sm" color="fg.muted">{detail}</Text>
      </Card.Body>
    </Card.Root>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
      <Text fontSize="sm" color="fg.muted">{children}</Text>
    </Box>
  );
}

function ExternalRow({
  row,
  onEdit,
  effectivePersonId,
}: {
  row: Record_;
  onEdit: () => void;
  effectivePersonId: Id<"persons"> | null;
}) {
  const remove = useMutation(api.participations.deleteExternal);
  const [busy, setBusy] = useState(false);

  async function onDelete() {
    setBusy(true);
    try {
      await remove({ participationId: row._id, effectivePersonId });
      toastSuccess("Record removed");
    } catch (error) {
      const { message, detail } = readableError(error, "The record could not be removed");
      toastError(message, detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body p={4}>
        <HStack justify="space-between" align="start" gap={4} wrap="wrap">
          <Stack gap={1} minW="16rem" flex="1">
            <HStack gap={2} wrap="wrap">
              <Text fontWeight="semibold">{row.external?.title}</Text>
              <Badge variant="subtle">{participationRoleLabel(row.role)}</Badge>
              <Badge variant="outline">
                {row.external?.kind === "course" ? "Course" : row.external?.kind === "conference" ? "Conference" : "Other"}
              </Badge>
            </HStack>
            <Text fontSize="sm" color="fg.muted">
              {row.startDay}
              {row.endDay === null || row.endDay === row.startDay ? "" : ` – ${row.endDay}`}
              {row.external?.organiser === "" ? "" : ` · ${row.external?.organiser}`}
              {row.external?.location === "" ? "" : ` · ${row.external?.location}`}
              {row.credits === null ? "" : ` · ${row.credits} ECTS`}
              {row.grade === null || row.grade === "" ? "" : ` · grade ${row.grade}`}
            </Text>
          </Stack>
          <HStack gap={1}>
            <IconButton aria-label="Edit" size="xs" variant="ghost" onClick={onEdit}><LuPencil /></IconButton>
            <IconButton
              aria-label="Remove"
              size="xs"
              variant="ghost"
              colorPalette="red"
              loading={busy}
              onClick={() => void onDelete()}
            >
              <LuTrash2 />
            </IconButton>
          </HStack>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
}

function ExternalDialog({
  open,
  existing,
  effectivePersonId,
  onClose,
}: {
  open: boolean;
  existing: Record_ | null;
  effectivePersonId: Id<"persons"> | null;
  onClose: () => void;
}) {
  const record = useMutation(api.participations.recordExternal);
  const update = useMutation(api.participations.updateExternal);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => emptyDraft());

  // Reset when the dialog opens on a different row. Keyed rather than watched,
  // because a draft that survived a close would reappear over the next one.
  const key = existing?._id ?? "new";
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setDraft(existing === null ? emptyDraft() : {
      kind: existing.external?.kind ?? "conference",
      title: existing.external?.title ?? "",
      organiser: existing.external?.organiser ?? "",
      location: existing.external?.location ?? "",
      url: existing.external?.url ?? "",
      role: existing.role,
      startDay: existing.startDay,
      endDay: existing.endDay ?? "",
      credits: existing.credits === null ? "" : String(existing.credits),
      grade: existing.grade ?? "",
      note: existing.note,
    });
  }

  // Caught here rather than left to the server. The mutation refuses it too
  // -- that is where the rule belongs -- but a round trip that comes back
  // saying "An occasion cannot end before it starts" over a form that still
  // shows both dates is a worse way to learn it.
  const endsBeforeItStarts = draft.endDay !== "" && draft.startDay !== ""
    && draft.endDay < draft.startDay;

  function set<K extends keyof typeof draft>(field: K, value: (typeof draft)[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  async function save() {
    setSaving(true);
    const payload = {
      external: {
        kind: draft.kind as never,
        title: draft.title,
        organiser: draft.organiser,
        location: draft.location,
        url: draft.url,
      },
      role: draft.role as never,
      startDay: draft.startDay,
      endDay: draft.endDay === "" ? null : draft.endDay,
      credits: draft.credits === "" ? null : Number(draft.credits),
      grade: draft.grade === "" ? null : draft.grade,
      note: draft.note,
      effectivePersonId,
    };
    try {
      if (existing === null) {
        const result = await record({ personId: null, ...payload });
        toastSuccess(
          "Recorded",
          result.satisfied === null
            ? draft.title
            : "It also completed a step that was waiting for exactly this.",
        );
      } else {
        await update({ participationId: existing._id, ...payload });
        toastSuccess("Record updated", draft.title);
      }
      onClose();
    } catch (error) {
      const { message, detail } = readableError(error, "The record could not be saved");
      toastError(message, detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose(); }} size="lg">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{existing === null ? "Record a conference or course" : "Edit the record"}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap={4}>
                <Text fontSize="sm" color="fg.muted">
                  Where an activity needs this checked, a reviewer confirms it.
                </Text>

                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>What</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field value={draft.kind} onChange={(e) => set("kind", e.target.value)}>
                        <option value="conference">Conference</option>
                        <option value="course">Course</option>
                        <option value="other">Other</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Your part in it</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field value={draft.role} onChange={(e) => set("role", e.target.value)}>
                        <option value="attendee">Attended</option>
                        <option value="posterPresenter">Presented a poster</option>
                        <option value="speaker">Gave a talk</option>
                        <option value="invitedLecturer">Invited lecture</option>
                        <option value="organiser">Organised it</option>
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </HStack>

                <Field.Root required>
                  <Field.Label>Name</Field.Label>
                  <Input value={draft.title} onChange={(e) => set("title", e.target.value)} />
                </Field.Root>

                <HStack gap={3}>
                  <Field.Root>
                    <Field.Label>Organiser</Field.Label>
                    <Input value={draft.organiser} onChange={(e) => set("organiser", e.target.value)} />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Where</Field.Label>
                    <Input value={draft.location} onChange={(e) => set("location", e.target.value)} />
                  </Field.Root>
                </HStack>

                <HStack gap={3}>
                  <Field.Root required>
                    <Field.Label>From</Field.Label>
                    <Input type="date" value={draft.startDay} onChange={(e) => set("startDay", e.target.value)} />
                  </Field.Root>
                  <Field.Root invalid={endsBeforeItStarts}>
                    <Field.Label>To</Field.Label>
                    <Input
                      type="date"
                      value={draft.endDay}
                      min={draft.startDay === "" ? undefined : draft.startDay}
                      onChange={(e) => set("endDay", e.target.value)}
                    />
                    {endsBeforeItStarts
                      ? <Field.ErrorText>It cannot end before it starts.</Field.ErrorText>
                      : <Field.HelperText>Leave blank for a single day.</Field.HelperText>}
                  </Field.Root>
                </HStack>

                {draft.kind !== "course" ? null : (
                  <HStack gap={3}>
                    <Field.Root>
                      <Field.Label>ECTS</Field.Label>
                      <Input
                        type="number"
                        min={0}
                        step="0.5"
                        value={draft.credits}
                        onChange={(e) => set("credits", e.target.value)}
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Grade</Field.Label>
                      <Input value={draft.grade} onChange={(e) => set("grade", e.target.value)} />
                    </Field.Root>
                  </HStack>
                )}

                <Field.Root>
                  <Field.Label>URL</Field.Label>
                  <Input value={draft.url} onChange={(e) => set("url", e.target.value)} />
                </Field.Root>

                <Field.Root>
                  <Field.Label>Note</Field.Label>
                  <Textarea rows={2} value={draft.note} onChange={(e) => set("note", e.target.value)} />
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                colorPalette="blue"
                loading={saving}
                disabled={draft.title.trim() === "" || draft.startDay === "" || endsBeforeItStarts}
                onClick={() => void save()}
              >
                {existing === null ? "Record it" : "Save"}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function emptyDraft() {
  return {
    kind: "conference",
    title: "",
    organiser: "",
    location: "",
    url: "",
    role: "attendee",
    startDay: "",
    endDay: "",
    credits: "",
    grade: "",
    note: "",
  };
}
